import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { SelectableLanguageCode } from "@/models/i18n"
import { defaultPercentFormatOptions, translateDecimal } from "@/utils/decimal"
import type { DecimalFormatOptions, DecimalOptions } from "@/utils/decimal"

/**
 * Formats a value as a percent.
 * @param formatOptions - The Intl.NumberFormat options to use.
 * @param options - Additional options for formatting.
 * @returns - The localized formatted percentage string.
 */
export const useFormatPercent = (
  formatOptions: DecimalFormatOptions = {},
  options: DecimalOptions = {}
) => {
  const { i18n } = useTranslation()

  const formatPercent = useCallback(
    (value: string | number | null | undefined) =>
      translateDecimal({
        format: {
          ...defaultPercentFormatOptions,
          ...formatOptions,
        },
        localeCode: i18n.language as SelectableLanguageCode,
        options,
        value,
      }),
    [i18n.language, formatOptions, options]
  )

  return useMemo(() => ({ formatPercent }), [formatPercent])
}
